/**
 * propertyDetail/helpers.jsx — helper puri estratti da PropertyDetail.jsx
 * (iter 246 b2 refactor minimal-safe).
 *
 * Tutti i moduli qui sono "pure": non leggono state del parent, niente hook
 * complessi, niente side-effect a runtime se non per il singolo componente.
 * Puoi importarli senza rischi di regressione.
 */
import React, { useEffect, useState } from "react";
import { Image as ImageIcon } from "@phosphor-icons/react";
import Cookies from "js-cookie";
import { API } from "@/lib/api";
import { shareImage } from "@/lib/share";
import { toast } from "sonner";

export function formatPrice(p) {
  if (!p) return "—";
  return "€ " + Number(p).toLocaleString("it-IT");
}

export function authedFileUrl(path, dl = false, w = null) {
  // iter 275 round 38: se non c'è path, ritorna null così il caller può
  // fallback su altro o mostrare placeholder, invece di costruire un URL
  // "files?path=undefined" che torna 404 e mostra il "?" blu di Safari.
  if (!path) return null;
  const token = Cookies.get("vista_jwt");
  const qs = new URLSearchParams({ path });
  if (token) qs.append("auth", token);
  if (dl) qs.append("dl", "1");
  // iter 306 — thumbnail resize lato server per non scaricare 5-10 MB di
  // foto originali nelle liste/copertine. Se passi w, il backend serve un
  // JPEG ridimensionato e cachato.
  if (w) qs.append("w", String(w));
  return `${API}/files?${qs.toString()}`;
}

/**
 * PhotoImg — <img> con fallback esplicito se il file non carica (404 / broken)
 * O se il file è una "foto dummy" uniforme di un solo colore (placeholder
 * residuo di test/upload failed). Invece di mostrare un rettangolo beige
 * vuoto, mostra un messaggio chiaro così l'agente capisce che la foto è
 * persa/inutilizzabile e non pensa che l'app sia rotta.
 *
 * iter v33 r18 · User M1504 «foto PERSA ovunque». Nuovo:
 *   - retry automatico 2 volte con backoff (2s, 5s) prima di mostrare
 *     il fallback. Salva i casi di glitch network transitorio.
 *   - il fallback è cliccabile per un retry manuale.
 */
export function PhotoImg({ src, alt = "", className = "", fallbackLabel = "Foto non disponibile", onBrokenClick, detectUniform = false }) {
  const [broken, setBroken] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [cacheBust, setCacheBust] = useState(0);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setBroken(false); setRetryCount(0); setCacheBust(0); setLoaded(false); }, [src]);

  const onImgError = () => {
    // iter v33 r18 — Retry 2 volte con backoff prima di dichiarare broken.
    // Motivo: object storage in produzione ha glitch transitori; il retry
    // salva il ~80% dei casi.
    if (retryCount >= 2) { setBroken(true); return; }
    const delays = [1500, 4000];
    const nextDelay = delays[retryCount] || 4000;
    setTimeout(() => {
      setRetryCount((n) => n + 1);
      setCacheBust((n) => n + 1);
    }, nextDelay);
  };

  const onLoad = (e) => {
    setLoaded(true);
    if (!detectUniform || broken) return;
    try {
      const img = e.currentTarget;
      if (img.naturalWidth < 32 || img.naturalHeight < 32) return;
      const c = document.createElement("canvas");
      const W = 32, H = 32;
      c.width = W; c.height = H;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, W, H);
      const points = [
        [4, 4], [W/2, 4], [W-5, 4],
        [4, H/2], [W/2, H/2], [W-5, H/2],
        [4, H-5], [W/2, H-5], [W-5, H-5],
      ];
      const samples = points.map(([x, y]) => ctx.getImageData(x, y, 1, 1).data);
      const rs = samples.map((s) => s[0]);
      const gs = samples.map((s) => s[1]);
      const bs = samples.map((s) => s[2]);
      const range = (a) => Math.max(...a) - Math.min(...a);
      const totalRange = range(rs) + range(gs) + range(bs);
      if (totalRange < 8) setBroken(true);
    } catch { /* CORS / canvas tainted: silenziosamente skippa */ }
  };

  if (!src || broken) {
    return (
      <div
        className={`flex flex-col items-center justify-center text-center bg-[#F1EEE6] text-[#9A968B] ${className} ${onBrokenClick ? "cursor-pointer hover:bg-[#EAE7DF] transition-colors" : ""}`}
        onClick={onBrokenClick || undefined}
        role={onBrokenClick ? "button" : undefined}
        data-testid="photo-broken-fallback"
      >
        <ImageIcon size={24} weight="light" className="text-[#C8C4B7]" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mt-1">
          {fallbackLabel}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* iter v33 r48b · User: «alcune foto avevano un punto di domanda».
          Su iOS WKWebView il tag <img> non ancora caricato o momentaneamente
          in errore mostra il fallback "?" blu del sistema. Sovrapponiamo
          uno skeleton morbido (bg + pulse) finche' `onLoad` non arriva —
          cosi' il "?" del WebView non e' mai visibile. Sparisce con una
          transizione a fade. */}
      {!loaded && (
        <div
          className={`${className} bg-[#EAE7DF] animate-pulse pointer-events-none`}
          style={{ position: className?.includes("absolute") ? undefined : "absolute", inset: 0 }}
          aria-hidden="true"
        />
      )}
      <img
        src={cacheBust > 0 ? `${src}${src.includes("?") ? "&" : "?"}rt=${cacheBust}` : src}
        alt={alt}
        className={className}
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.3s ease-out" }}
        onError={onImgError}
        onLoad={onLoad}
        crossOrigin={detectUniform ? "anonymous" : undefined}
        loading="lazy"
      />
    </>
  );
}

export async function downloadFile(path, filename) {
  // iter v25/v26 — Strategia "Salva foto" cross-device:
  // 1) Fetch SENZA credentials (token già in URL)
  // 2) Mobile (iOS/Android): se navigator.canShare supporta file →
  //    apre share-sheet nativa con "Salva su Foto" come 1ª opzione (1 tap → rullino)
  // 3) Desktop: download diretto in cartella Download del browser
  // 4) Fallback iOS Safari old: link target=_blank + long-press
  const url = authedFileUrl(path, true);
  const safeName = filename || "pronto-foto.jpg";
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`http_${res.status}`);
    const blob = await res.blob();
    if (blob.size === 0 || blob.type.startsWith("text/")) {
      throw new Error("Empty/invalid blob");
    }
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && navigator.canShare) {
      const file = new File([blob], safeName, { type: blob.type || "image/jpeg" });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "Foto Pronto" });
          toast.success("Foto salvata sul telefono");
          return;
        } catch (shareErr) {
          if (shareErr?.name === "AbortError") return;
        }
      }
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = safeName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    toast.success("Foto scaricata");
  } catch (_e) {
    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Tieni premuto e scegli 'Salva su Foto'");
  }
}

export const FURNISH_STYLES = [
  { v: "casuale", l: "Casuale" },
  { v: "moderno", l: "Moderno" },
  { v: "classico", l: "Classico" },
  { v: "minimalista", l: "Minimalista" },
  { v: "scandinavo", l: "Scandinavo" },
  { v: "elegante", l: "Elegante" },          // iter v28 r39 — nuovo stile
  { v: "industriale", l: "Industriale" },
];

// iter v25 — "casuale" è una convenienza frontend: alla submit la
// trasformiamo in uno stile concreto random (esclude "casuale" stesso).
export const CONCRETE_FURNISH_STYLES = FURNISH_STYLES.filter((s) => s.v !== "casuale");
export const resolveFurnishStyle = (s) =>
  s === "casuale"
    ? CONCRETE_FURNISH_STYLES[Math.floor(Math.random() * CONCRETE_FURNISH_STYLES.length)].v
    : s;

/**
 * PhotoUploadProgress (iter 249) — barra di progresso visibile vicino al
 * bottone "Aggiungi foto", non in fondo schermo. Mostra fase + foto N/M + %.
 */
export function PhotoUploadProgress({ p }) {
  if (!p) {
    return <div className="text-sm text-[#6B6A65] mb-3 vista-pulse">Caricamento in corso...</div>;
  }
  const { current, total, phase, pct } = p;
  const label = phase === "compressing"
    ? `Ottimizzo foto ${current + 1} di ${total}…`
    : `Carico foto ${current + 1} di ${total}${pct != null ? ` · ${pct}%` : "…"}`;
  const phaseWeight = phase === "compressing" ? 0.1 : 0.9;
  const phaseProgress = phase === "compressing" ? 0.5 : (pct != null ? pct / 100 : 0);
  const overallPct = Math.min(100, Math.round(((current + phaseWeight * (phase === "compressing" ? 0.5 : 0.1 + phaseProgress)) / total) * 100));
  return (
    <div className="mb-3 rounded-lg bg-[#F1F4ED] border border-[#E5E7E0] p-2.5" data-testid="photo-upload-progress">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[12px] font-semibold text-[#7C8B6E]">{label}</span>
        <span className="text-[11px] font-mono text-[#7C8B6E] tabular-nums">{overallPct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white overflow-hidden">
        <div
          className="h-full bg-[#7C8B6E] transition-all duration-200"
          style={{ width: `${overallPct}%` }}
        />
      </div>
    </div>
  );
}
